import { Box, Button, Stack } from "@mui/material";
import { useState } from "react";
import InfoSubTitle from "@/Settings/components/SystemSettings/common/InfoSubTitle";
import TermsAndConditionDialog from "./TermsAndConditionsPopup";
import { IPayplanTermConditions } from "@/Settings/types/Common/model";
import { useMutation } from "@tanstack/react-query";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import { useSelector } from "react-redux";
import { IUser } from "@/DataPages/types/timeAndPayRates/models";
import { errorLogger } from "@/Common/helpers/logger";
import clsx from "clsx";
import { IMappedPosition } from "@/Common/types/positions/api";

type ITermsAndCondProps = {
	locationId: string;
	position: string;
	mappedPositions?: IMappedPosition[];
	payplanTermConditions?: IPayplanTermConditions[];
};

const TermsAndConditions = (props: ITermsAndCondProps) => {
	const { locationId, position, payplanTermConditions, mappedPositions } = props;
	const user = useSelector(
		(state: {
			user: {
				user: IUser;
			};
		}) => state.user.user
	);

	const { mutate } = useMutation({
		mutationFn: async (input: { position: string; inEditionBy: string }) => {
			await updateSystemSettings(locationId, { payplanTermConditions: input }, "TERMS_AND_CONDITIONS");
		},
		onError: (error) => errorLogger(error),
	});

	const [dialogOpen, setDialogOpen] = useState(false);

	const handlerDialog = (dialogOpenArg: boolean) => {
		setDialogOpen(dialogOpenArg);
	};

	const handlerFocus = () => {
		mutate({
			position,
			inEditionBy: user._id,
		});
	};

	const userEditionInfo = payplanTermConditions?.find((terms) => terms.position === position)?.inEditionBy;

	const termsAndConditionByPosition = payplanTermConditions?.find((item) => item.position === position)?.termConditions || [];

	const allowEditionByUserActions = user.actions.includes("EDIT_TERMS_AND_CONDITIONS");

	const isDepartmentalPosition = mappedPositions?.find((pos) => pos.systemPosition === position)?.isDepartmental ?? false;

	if (dialogOpen) {
		return (
			<TermsAndConditionDialog
				locationId={locationId}
				dialogOpen={dialogOpen}
				position={position}
				isDepartmental={isDepartmentalPosition}
				handlerDialog={handlerDialog}
				termsAndConditionProps={termsAndConditionByPosition}
				handlerFocus={handlerFocus}
				inEditionBy={userEditionInfo}
				allowEditionByUserActions={allowEditionByUserActions}
			/>
		);
	}

	return (
		<Stack>
			<InfoSubTitle title="Pay Plan Terms & Conditions Configuration" className="!mb-[24px]" />
			<Box sx={{ width: "fit-content" }}>
				<Button
					variant="contained"
					className={clsx({
						"!rounded-md !py-[5px] !text-sm !text-nowrap !text-white !capitalize !font-sans": true,
					})}
					sx={{
						maxWidth: "max-content",
						boxShadow: "none",
						bgcolor: "var(--primary-color)",
						"&:hover": {
							bgcolor: "var(--primary-color)",
							boxShadow: "0 0 10px 2px var(--primary-color-opacity-50)",
						},
						"&:disabled": {
							backgroundColor: "var(--disabled-and-stroke-color)",
						},
					}}
					onClick={() => {
						handlerDialog(true);
					}}
				>
					Configure Terms & Conditions
				</Button>
			</Box>
		</Stack>
	);
};

export default TermsAndConditions;
